import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Layout, Popover, Spin } from 'antd';
import { SideMenu } from "./SideMenu";
import { Redirect, useParams } from "react-router-dom";
import { MENU } from "./constants";
import { NewsFeedView } from "../newsFeed/NewsFeedView";
import { NewPost } from "../newsFeed/NewPost";
import { InductionsView } from "../inductionVideos/InductionsView";
import { BuildingContactsView } from "../buildingContacts/BuildingContactsView";
import { Users } from "../users/Users";
import { logout } from "../login/actions";
import { sessionStore, SESSION_ATTRS } from "../store/session";
import NonAdminUserView from "./NonAdminUserView";
import InactiveUserView from "./InactiveUserView";
import { isDesktop } from '../utils/view'
import { MenuOutlined } from '@ant-design/icons';
import { BookingSpaceView } from "../bookingSpace/BookingSpaceView";
import { IssuesView } from "../issues/IssuesView";
import { DeskBookingView } from "../deskBooking/DeskBookingView";
import { ProtectedFeature } from "../components/ProtectedFeature";
import { FeatureManagementView } from "../featureManagement/FeatureManagementView";
import { ServiceNowSettingsView } from "../serviceNowSettings/ServiceNowSettingsView";
import { InceptionSettingsView } from "../inceptionSettings/InceptionSettingsView";
import { VisitorManagementView } from "../visitorManagement/VisitorManagementView";
import { getConfiguredInceptionTenants } from "../visitorManagement/actions";
import { isTenantVisitorPassDelegationEnabled } from "../utils/tenantFeatures";

const { Header } = Layout;

export const DashBoardPage = () => {
    let { mainMenu, subMenu, first_param, second_param } = useParams<{ mainMenu: string, subMenu: string, first_param?: string, second_param?: string }>();

    const { state } = useContext(sessionStore);
    const [collapsed, setCollapsed] = useState(false);
    const user: any = state.get(SESSION_ATTRS.LOGGED_USER);
    const loggedTenant: any = state.get(SESSION_ATTRS.LOGGED_TENANT);
    const tenants = state.get(SESSION_ATTRS.TENANTS) as any[] | undefined;
    const visitorPassEnabledTenantIds = useMemo(
        () => new Set((tenants || []).filter(isTenantVisitorPassDelegationEnabled).map((tenant: any) => tenant.tenantId)),
        [tenants]
    );
    const [showVisitorManagement, setShowVisitorManagement] = useState(false);
    const [visitorManagementVisibilityLoaded, setVisitorManagementVisibilityLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadVisitorManagementVisibility = async () => {
            setVisitorManagementVisibilityLoaded(false);
            if (!user?.isAdmin || !user?.buildingId || visitorPassEnabledTenantIds.size === 0) {
                setShowVisitorManagement(false);
                setVisitorManagementVisibilityLoaded(true);
                return;
            }

            try {
                const response = await getConfiguredInceptionTenants(user.buildingId);
                if (isMounted) {
                    setShowVisitorManagement(
                        Array.isArray(response?.tenants) &&
                        response.tenants.some((tenant: any) => visitorPassEnabledTenantIds.has(tenant.tenantId))
                    );
                    setVisitorManagementVisibilityLoaded(true);
                }
            } catch (error) {
                console.error("Unable to resolve Visitor Management visibility", error);
                if (isMounted) {
                    setShowVisitorManagement(false);
                    setVisitorManagementVisibilityLoaded(true);
                }
            }
        };

        loadVisitorManagementVisibility();

        return () => {
            isMounted = false;
        };
    }, [user?.buildingId, user?.isAdmin, visitorPassEnabledTenantIds]);

    let getView = () => {
        if (user.isInvalid || !user.isActive || user.isFederatedUser) {
            return <InactiveUserView />
        }
        if (!user.isAdmin) {
            return <NonAdminUserView />
        }
        switch (mainMenu) {
            case MENU.USERS:
                return <ProtectedFeature feature="users"><Users /></ProtectedFeature>;
            case MENU.NEWS_FEED.ROOT:
                if (subMenu) {
                    if (subMenu === MENU.NEWS_FEED.SUB.ADD_NEW)
                        return <ProtectedFeature feature="newsFeed"><NewPost /></ProtectedFeature>;
                    if (subMenu === MENU.NEWS_FEED.SUB.EDIT)
                        return <ProtectedFeature feature="newsFeed"><NewPost buildingId={first_param} newsId={second_param} /></ProtectedFeature>;
                }
                return <ProtectedFeature feature="newsFeed"><NewsFeedView /></ProtectedFeature>;
            case MENU.BOOKING_SPACE.ROOT:
                return <ProtectedFeature feature="bookingSpace"><BookingSpaceView /></ProtectedFeature>;
            case MENU.DESK_BOOKING.ROOT:
                return <ProtectedFeature feature="deskBooking"><DeskBookingView /></ProtectedFeature>;
            case MENU.INDUCTION_VIDEOS.ROOT:
                return <ProtectedFeature feature="inductions"><InductionsView /></ProtectedFeature>;
            case MENU.BUILDING_CONTACTS:
                return <ProtectedFeature feature="buildingContacts"><BuildingContactsView /></ProtectedFeature>;
            case MENU.ISSUES:
                return <ProtectedFeature feature="issues"><IssuesView /></ProtectedFeature>;
            case MENU.VISITOR_MANAGEMENT:
                if (!visitorManagementVisibilityLoaded) {
                    return <div className="loading-wrapper"><Spin tip="Loading..." /></div>;
                }
                if (!showVisitorManagement) {
                    return <Redirect to={`/${MENU.USERS}`} />;
                }
                return <ProtectedFeature feature="visitorManagement"><VisitorManagementView /></ProtectedFeature>;
            case MENU.FEATURE_MANAGEMENT:
                return <ProtectedFeature feature="featureManagement"><FeatureManagementView /></ProtectedFeature>;
            case MENU.SERVICE_NOW_SETTINGS:
                if (!user.serviceNowAdmin) {
                    return <Redirect to={`/${MENU.USERS}`} />;
                }
                return <ProtectedFeature feature="serviceNowSettings"><ServiceNowSettingsView /></ProtectedFeature>;
            case MENU.INCEPTION_SETTINGS:
                return <ProtectedFeature feature="inceptionSettings"><InceptionSettingsView /></ProtectedFeature>;
            default:
                return <Redirect to={`/${MENU.USERS}`} />;
        }
    };

    const onLogoutClick = async () => {
        await logout();
        window.location.reload();
    }

    const userIcon = () => {
        const getFirstChar = (name: string) => name ? name.substr(0, 1) : "";
        return `${getFirstChar(user.firstName)}${getFirstChar(user.lastName)}`;
    }

    const toggle = () => {
        setCollapsed(collapsed => !collapsed);
    }
  
    const sideMenu = (!user.isInvalid && user.isActive && !user.isFederatedUser && user.isAdmin) ?
        <SideMenu
            menuCollapsed={isDesktop() ? false : collapsed}
            selectedMenu={mainMenu}
            isDesktop={isDesktop()}
            toggle={toggle}
            logoUrl={loggedTenant.logoUrl}
            serviceNowAdmin={user.serviceNowAdmin === true}
            showVisitorManagement={showVisitorManagement} />
        : null;


    const mobileMenuToggler = !isDesktop() ? <div className="menu-toggler">
        <Button className="menu-toggle-button" icon={<MenuOutlined className="menu-toggle-icon" />} onClick={toggle} />
    </div> : null;

    return (
        <Layout className={!isDesktop() ? "layout--mobile" : ""}>
            {sideMenu}
            <Layout>
                <Header style={{ background: 'white' }}>
                    <div>{mobileMenuToggler}</div>
                    <div className="login-info">
                        <a href="/" className="profile-link">
                            {`${user.firstName} ${user.lastName}`}
                            <Popover placement="bottomRight" content={
                                <div>
                                    <Button type="link" onClick={onLogoutClick}>Logout</Button>
                                </div>
                            } trigger="click">
                                <span>{userIcon()}</span>
                            </Popover>
                        </a>
                    </div>
                </Header>
                <Layout className="main-content-wrapper">
                    {getView()}
                </Layout>
                {/* <Footer>Footer</Footer> */}
            </Layout>
        </Layout>
    );
}
